import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subject, catchError, concatMap, defaultIfEmpty, distinctUntilChanged, filter, map, of, switchMap, take, takeUntil, timeout, withLatestFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { selectAuthState } from '../store/selectors/auth.selectors';
import { selectCarState } from '../store/selectors/car.selectors';
import { selectRealEstateState } from '../store/selectors/real-estate.selectors';
import * as Cars from '../store/actions/car.actions';
import * as Properties from '../store/actions/real-estate.actions';
import { buildSearchRequest, withoutViewport } from '../models/search-request';
import { SearchDomain, SearchHistoryEntry, normalizeHistory } from './search-history.model';
import { resetSearchHistorySession, restoreExecutedSearch } from './search-history.actions';

@Injectable({ providedIn: 'root' })
export class SearchHistoryService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);
  private readonly router = inject(Router);
  private readonly destroy = inject(DestroyRef);
  private readonly url = environment.apiUrl + '/api/search-history';
  private readonly auth = this.store.selectSignal(selectAuthState);
  private readonly cars = this.store.selectSignal(selectCarState);
  private readonly properties = this.store.selectSignal(selectRealEstateState);
  private readonly initialized = new Set<string>();
  private readonly ownerChanged = new Subject<void>();
  private readonly interacted = new Subject<SearchDomain>();
  private readonly revisions = { CAR: 0, REAL_ESTATE: 0 };
  readonly persistenceError = signal(false);

  constructor() {
    this.store.select(selectAuthState).pipe(map(auth => auth.user?.id ?? null), distinctUntilChanged(), takeUntilDestroyed(this.destroy))
      .subscribe(() => { this.initialized.clear(); this.persistenceError.set(false); this.ownerChanged.next(); this.store.dispatch(resetSearchHistorySession()); });
    this.actions.pipe(takeUntilDestroyed(this.destroy)).subscribe(action => {
      const domain = this.domainFor(action.type);
      if (domain && !action.type.endsWith('Success') && !action.type.endsWith('Failure')) {
        this.revisions[domain]++; this.interacted.next(domain);
      }
    });
    this.actions.pipe(
      filter(action => [Cars.searchCars.type, Cars.searchMapCars.type, Cars.setCarPolygon.type,
        Properties.searchRealEstates.type, Properties.searchMapRealEstates.type, Properties.setRealEstatePolygon.type].includes(action.type) ||
        (action.type === restoreExecutedSearch.type && (action as ReturnType<typeof restoreExecutedSearch>).recordHistory === true)),
      map(action => {
        const restore = action.type === restoreExecutedSearch.type ? action as ReturnType<typeof restoreExecutedSearch> : undefined;
        const domain: SearchDomain = restore?.domain ?? (this.domainFor(action.type)!);
        const state = domain === 'CAR' ? this.cars() : this.properties();
        const mode = restore?.mode ?? ([Cars.searchMapCars.type, Cars.setCarPolygon.type, Properties.searchMapRealEstates.type, Properties.setRealEstatePolygon.type].includes(action.type) ? 'map' : 'list');
        return { owner: this.auth().user?.id, entry: normalizeHistory({ domain, mode,
          criteria: buildSearchRequest<object>(state, mode === 'map') as unknown as Record<string, unknown>, viewport: state.mapViewport }) };
      }),
      concatMap(({ owner, entry }) => !owner ? EMPTY : this.store.select(selectAuthState).pipe(
        filter(auth => !auth.loadingUser && !auth.logingIn), take(1),
        switchMap(auth => owner !== auth.user?.id ? EMPTY : this.http.post<void>(this.url, { ...entry, executionId: crypto.randomUUID() })),
          takeUntil(this.ownerChanged),
          map(() => { this.persistenceError.set(false); }),
          catchError(() => { this.persistenceError.set(true); return EMPTY; })
        )),
      takeUntilDestroyed(this.destroy)
    ).subscribe();
  }

  /** Called by route guards: finish restoration before either mode is rendered. */
  enter(domain: SearchDomain, route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.store.select(selectAuthState).pipe(
      filter(auth => !auth.loadingUser && !auth.logingIn), take(1),
      switchMap(auth => {
        const owner = auth.user?.id;
        const key = (owner ?? 'guest') + ':' + domain;
        const explicit = this.explicit(domain, route);
        if (explicit) {
          this.initialized.add(key); this.store.dispatch(restoreExecutedSearch(explicit));
          return of(true);
        }
        if (this.initialized.has(key)) return of(true);
        const revision = this.revisions[domain];
        const navigation = this.router.getCurrentNavigation()?.id;
        const load = owner ? this.http.get<SearchHistoryEntry[]>(this.url + '/' + domain).pipe(timeout(8000)) : of([]);
        return load.pipe(
          // An edit, explicit search, mode change, logout or cancelled navigation wins.
          takeUntil(this.interacted.pipe(filter(changed => changed === domain))), takeUntil(this.ownerChanged),
          catchError(() => of([])),
          map(entries => {
            if (this.auth().user?.id !== owner || this.revisions[domain] !== revision ||
              (navigation !== undefined && this.router.getCurrentNavigation()?.id !== navigation)) return true;
            const latest = entries.find(entry => entry.domain === domain && ['list', 'map'].includes(entry.mode) &&
              entry.criteria && typeof entry.criteria === 'object' && !Array.isArray(entry.criteria));
            this.initialized.add(key);
            this.store.dispatch(restoreExecutedSearch(normalizeHistory(latest ?? { domain, mode: 'list', criteria: {} })));
            return true;
          }), defaultIfEmpty(true)
        );
      })
    );
  }

  private explicit(domain: SearchDomain, route: ActivatedRouteSnapshot): (SearchHistoryEntry & { recordHistory?: boolean }) | undefined {
    const navigation = this.router.getCurrentNavigation()?.extras.state ?? {};
    const query = route.queryParams ?? {};
    const requestedMode = route.data?.['searchMode'] ?? query['mode'] ?? navigation['searchMode'];
    const encoded = query['criteria'] ?? navigation['criteria'] ?? navigation['query'] ?? navigation['mapQuery'];
    const fields = Object.fromEntries(Object.entries(query).filter(([key]) => !['mode', 'criteria'].includes(key) && !key.startsWith('utm_')));
    if (!requestedMode && !encoded && !Object.keys(fields).length) return undefined;
    let criteria: Record<string, unknown> = {};
    try { criteria = typeof encoded === 'string' ? JSON.parse(encoded) : encoded ?? fields; } catch { criteria = {}; }
    if (!criteria || typeof criteria !== 'object' || Array.isArray(criteria)) criteria = {};
    criteria = Object.fromEntries(Object.entries(criteria).map(([key, value]) => {
      if (typeof value === 'string') {
        try { return [key, JSON.parse(value)]; } catch { /* Plain text/lookup values. */ }
        if (['models', 'bodyTypes', 'carUseTypes', 'driveTypes', 'seatCovers', 'fuelTypes', 'colors', 'interiorColors', 'conditions', 'statuses', 'locations', 'propertyTypes', 'typeOfPossessions'].includes(key)) return [key, value.split(',')];
      }
      return [key, value];
    }));
    const hasCriteria = !!encoded || Object.keys(fields).length > 0;
    if (!hasCriteria) {
      const state = domain === 'CAR' ? this.cars() : this.properties();
      const map = domain === 'CAR' ? this.cars().isCarMapView : this.properties().isRealEstateMapView;
      criteria = withoutViewport((map ? state.mapDraft ?? state.mapQuery : state.listDraft ?? state.query) ?? {}) as Record<string, unknown>;
      if (requestedMode === 'map') {
        const previous = state.mapQuery as unknown as Record<string, unknown> | undefined;
        const geometry = state.activePolygon?.length ? { polygonFilter: state.activePolygon } :
          Object.fromEntries(['northEastLat', 'northEastLng', 'southWestLat', 'southWestLng'].map(key => [key, previous?.[key]]));
        criteria = { ...criteria, ...geometry };
      }
    }
    const map = requestedMode ? requestedMode === 'map' : query['isMapView'] === 'true' || !!navigation['mapQuery'] || 'northEastLat' in criteria || 'polygonFilter' in criteria;
    const viewport = !hasCriteria && map ? (domain === 'CAR' ? this.cars() : this.properties()).mapViewport : undefined;
    return { ...normalizeHistory({ domain, mode: map ? 'map' : 'list', criteria, viewport }), recordHistory: true };
  }

  private domainFor(type: string): SearchDomain | undefined {
    if (Object.values(Cars).some(action => action.type === type)) return 'CAR';
    if (Object.values(Properties).some(action => action.type === type)) return 'REAL_ESTATE';
    return undefined;
  }
}
